/**
 * Types for the v2 final report.
 *
 * v1 (`src/pages/RaketechFinalReport.tsx`) values each account at the best of
 * three sources we could evidence ourselves — the original reconciliation, what
 * we synced from operator backends, and manual backend checks — and compares
 * that to what Raketech invoiced.
 *
 * v2 adds a fourth source: Raketech's own replacement workbook. Because that
 * figure is their own admission it is the hardest of the four for them to
 * dispute, and for 91 accounts it is higher than anything we could previously
 * evidence. It also covers Dec 2024 – May 2026, which v1 could only reach via a
 * live Affiliation Cloud session.
 */

import type { NewReconOffer } from './raketechReconDiff.types';

/** Where an account's earnings figure came from, in ascending order of how
 *  hard it is for Raketech to argue with. */
export type ClaimSource =
  | 'reconciliation'
  | 'synced'
  | 'manual_check'
  | 'raketech_restated';

export const CLAIM_SOURCE_LABELS: Record<ClaimSource, string> = {
  reconciliation: 'Original reconciliation',
  synced: 'Synced from operator',
  manual_check: 'Manual backend check',
  raketech_restated: 'Raketech restated',
};

/** One candidate earnings figure for an account, before the best is picked. */
export interface SourceCandidate {
  source: ClaimSource;
  /** EUR, already on our net-of-margin basis. */
  amount: number;
  /** Present when the source figure was in another currency. */
  originalAmount?: number;
  originalCurrency?: string;
  /** True when a 5% partner margin was applied to reach `amount`. */
  marginApplied: boolean;
  available: boolean;
}

export interface ClaimRow {
  key: string;
  /** Raketech's account id. Equals `HUB ID` in the new workbook. */
  accountId: string;
  acIds: string[];
  username: string;
  partner: string;
  brands: string[];
  geos: string[];
  currency: string;

  /** True when this account exists only in the new workbook, so v1 never saw it. */
  newToThisReport: boolean;
  /** True when we hold no adwstats account for it and cannot verify independently. */
  missingFromAdwstats: boolean;

  invoicedRecon: number;
  invoicedPostRecon: number;
  invoicedTotal: number;

  candidates: SourceCandidate[];
  /** The winning candidate over the reconciled period. */
  chosenSource: ClaimSource;
  earnedReconPeriod: number;
  /** Dec 2024 onward, from Raketech's own monthly columns. */
  earnedTail: number;
  /** What v1 would have used for the tail, when an AC session supplied it. */
  earnedTailFromAc: number;
  earnedTotal: number;

  /** Negative means underpaid: invoiced less than earned. */
  underpaid: number;
  underpaidPct: number;

  /** How much of the shortfall exists only because of their restatement. */
  attributableToRestatement: number;

  offers: NewReconOffer[];
  monthly: ClaimMonth[];
  /** Raketech's own invoice lines for this account, for the drilldown. */
  invoiceEntries: ClaimInvoiceEntry[];
}

/** One invoice line Raketech raised, as `/api/raketech/compare` matched it. */
export interface ClaimInvoiceEntry {
  label: string;
  ftds: number;
  revenue: number;
  monthly: { month: string; ftds: number; revenue: number }[];
}

/**
 * One month of an account, carrying every figure we hold for it.
 *
 * `earned` is the only field that feeds the claim. The rest are shown side by
 * side so the drilldown is a complete picture of the evidence rather than just
 * the number that won.
 */
export interface ClaimMonth {
  month: string;
  invoiced: number;
  ftds: number;
  /** Best evidenced earnings for the month. This is what the claim uses. */
  earned: number;

  /** View only: the original reconciliation's figure for the month. */
  oldRecon: number;
  /** View only: what we synced from the operator, net of the 5% margin. */
  synced: number;
  /** View only: Affiliation Cloud's reported commission. */
  ac: number;
  /** Raketech's restated figure from the new workbook. */
  restated: number;

  underpaid: number;
  /** True when nothing supports the month on either side, so it cannot be judged. */
  unsupported: boolean;
}

export interface ClaimSummary {
  reconMonths: string[];
  tailMonths: string[];

  accounts: number;
  accountsNewToReport: number;
  accountsMissingFromAdwstats: number;

  invoicedTotal: number;
  earnedTotal: number;
  underpaidTotal: number;

  /** Same computation with the restated source withheld, for the bridge. */
  underpaidWithoutRestatement: number;
  restatementEffect: number;

  bySource: Record<ClaimSource, { accounts: number; earned: number; underpaid: number }>;

  /** Accounts we could not verify independently, and their share of the claim. */
  unverifiableEarnings: number;
  unverifiableUnderpaid: number;
}

/** One line reconciling Raketech's stated position to this report's claim. */
export interface ClaimBridgeStep {
  label: string;
  amount: number | null;
  kind: 'figure' | 'subtotal' | 'addition' | 'deduction' | 'result';
  note?: string;
  /** Set when Raketech themselves state the figure, rather than us deriving it. */
  theirFigure?: boolean;
}
